import { CustomBottomBar, CustomPageTitle, FileItem, MinimalStepper } from '@components';
import { Button, Container, CustomIcon, Icon, Typography, playIcon } from '@design-system';
import { useVisibilityRerender } from '@hooks';
import { TalkVideo } from '@services';
import React, { useCallback, useRef } from 'react';
import { BiSolidChat } from 'react-icons/bi';
import { FaRegFilePdf } from 'react-icons/fa6';
import { GrDownload, GrFormCheckmark } from 'react-icons/gr';
import { MdKeyboardArrowRight } from 'react-icons/md';
import ReactPlayer from 'react-player';
import { useNavigate } from 'react-router-dom';
import './CauseriesTalkFilesPage.scss';
import { CauseriesTalkFilesSkeleton } from './CauseriesTalkFilesSkeleton';
import { useCauseriesTalkFilesPageVM } from './useCauseriesTalkFilesPageVM';

export const CauseriesTalkFilesPage: React.FC = () => {
    const {
        thematicId,
        talkId,
        pageTitle,
        files,
        videos,
        isLoading,
        errorMessage,
        acknowledgeFile,
        acknowledgeVideo,
        areFilesAcknowledged,
        isEveryAssetAcknowledged
    } = useCauseriesTalkFilesPageVM();

    const navigate = useNavigate();
    const clickedVideoRef = useRef<TalkVideo | null>(null);

    const handleVisibilityReturn = useCallback(() => {
        if (clickedVideoRef.current !== null) {
            acknowledgeVideo(clickedVideoRef.current);
            clickedVideoRef.current = null;
        }
    }, [acknowledgeVideo]);

    const videoRerenderKey = useVisibilityRerender(handleVisibilityReturn);

    if ((!files || !videos) && errorMessage) {
        return (
            <Container>
                <Typography intrinsic="p" color="red">
                    {errorMessage}
                </Typography>
            </Container>
        );
    }

    return (
        <div className={`causeries-talk-files-page ${isLoading ? 'loading' : ''}`}>
            {pageTitle && (
                <CustomPageTitle>
                    <Typography intrinsic="h3" iconColor="blue" icon={<BiSolidChat />} iconFilled iconSize={20}>
                        {pageTitle}
                    </Typography>
                </CustomPageTitle>
            )}

            <Container className="files-page-header" radius="none" column>
                <Typography intrinsic="h2" className="mb-large-spacing">
                    Les instructions
                </Typography>
                <MinimalStepper
                    steps={[
                        { label: 'Les instructions', status: isEveryAssetAcknowledged ? 'completed' : 'inProgress' },
                        { label: 'Le quizz', status: 'pending' },
                        { label: 'Emargement', status: 'pending' }
                    ]}
                />
            </Container>

            {isLoading ? (
                <CauseriesTalkFilesSkeleton />
            ) : (
                <>
                    <Container>
                        {videos.map((video, index) => (
                            <Container key={video.id} bordered="grey" className="video-container">
                                <Typography intrinsic="h4" defaultSpacing>
                                    Vidéo {videos.length > 1 ? `(${index + 1})` : ''}
                                </Typography>
                                <ReactPlayer
                                    key={`${video.id}-${videoRerenderKey}`}
                                    url={video.url}
                                    className="video-player"
                                    controls
                                    light
                                    playing={true}
                                    onClickPreview={() => {
                                        clickedVideoRef.current = video;
                                    }}
                                    onPlay={() => {
                                        setTimeout(() => {
                                            acknowledgeVideo(video);
                                            clickedVideoRef.current = null;
                                        }, 5000);
                                    }}
                                    playIcon={<CustomIcon src={playIcon} alt="play" />}
                                    config={{
                                        youtube: {
                                            playerVars: {
                                                playsinline: 1
                                            }
                                        }
                                    }}
                                />
                                <Typography intrinsic="p" color="marine" className="pl-small-spacing mt-small-medium-spacing">
                                    {video.name}
                                </Typography>
                                {video.consulted && <Icon icon={<GrFormCheckmark />} size={28} color="green" className="video-check" />}
                            </Container>
                        ))}

                        <Container bordered="grey" className="files-container">
                            <Typography intrinsic="h4" defaultSpacing>
                                Documentation
                            </Typography>
                            {files.map(({ id, name, path, consulted }) => (
                                <a href={path} target="_blank" rel="noopener noreferrer" key={id}>
                                    <FileItem
                                        title={name}
                                        icon={<Icon icon={<FaRegFilePdf />} size={20} />}
                                        onClick={async () => {
                                            acknowledgeFile({ id, name, path });
                                        }}
                                        customRightIcon={consulted ? null : <Icon icon={<GrDownload />} size={20} />}
                                        className={`${consulted ? 'bg-grey4' : ''}`}
                                    />
                                </a>
                            ))}
                            {areFilesAcknowledged && <Icon icon={<GrFormCheckmark />} size={28} color="green" className="file-check" />}
                        </Container>
                    </Container>
                </>
            )}

            <CustomBottomBar>
                <Button
                    color="marine"
                    variant="primary"
                    size="fullwidth"
                    icon={<MdKeyboardArrowRight />}
                    iconPosition="forced-right"
                    iconColor="white"
                    iconSize={30}
                    className="bottom-bar-button"
                    disabled={!isEveryAssetAcknowledged}
                    onClick={() => {
                        navigate(`/sante-securite/causeries/list/${thematicId}/questions/${talkId}`);
                    }}
                >
                    <Typography intrinsic="p">Passez à l'étape suivante</Typography>
                </Button>
            </CustomBottomBar>
        </div>
    );
};
